import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fdate } from "@/lib/api";
import { PageHeader, StatusBadge, TableWrap, Th, Td, Empty, Field, NSelect } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export const EMPTY_EMP = { first_name: "", last_name: "", position: "", department: "", phone: "", email: "", hire_date: "", contract_type: "CDI", status: "actif", notes: "" };
export const DEPARTMENTS = ["Direction", "Génie civil", "Génie électrique", "Transport & logistique", "Formation", "Informatique", "Administration & finances"];

export const EmployeeDialog = ({ open, initial, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY_EMP);
  useEffect(() => { if (open) setF({ ...EMPTY_EMP, ...(initial || {}) }); }, [open, initial]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const body = Object.fromEntries(Object.keys(EMPTY_EMP).map((k) => [k, f[k] ?? ""]));
    try {
      const r = initial?.id ? await api.put(`/employees/${initial.id}`, body) : await api.post("/employees", body);
      toast.success("Fiche employé enregistrée");
      onSaved(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="employee-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Modifier l'employé" : "Nouvel employé"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Prénom *"><Input value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="emp-first-name-input" /></Field>
          <Field label="Nom *"><Input value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="emp-last-name-input" /></Field>
          <Field label="Poste *"><Input value={f.position} onChange={(e) => set("position", e.target.value)} data-testid="emp-position-input" /></Field>
          <Field label="Département">
            <NSelect value={f.department} onChange={(v) => set("department", v)} testId="emp-department-select">
              <option value="">—</option>{DEPARTMENTS.map((d) => <option key={d}>{d}</option>)}
            </NSelect>
          </Field>
          <Field label="Téléphone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="emp-phone-input" /></Field>
          <Field label="Email"><Input value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="emp-email-input" /></Field>
          <Field label="Date d'embauche"><Input type="date" value={f.hire_date} onChange={(e) => set("hire_date", e.target.value)} data-testid="emp-hire-date-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contrat"><NSelect value={f.contract_type} onChange={(v) => set("contract_type", v)} testId="emp-contract-select">{["CDI", "CDD", "Journalier", "Stage", "Consultant"].map((c) => <option key={c}>{c}</option>)}</NSelect></Field>
            <Field label="Statut"><NSelect value={f.status} onChange={(v) => set("status", v)} testId="emp-status-select"><option value="actif">Actif</option><option value="conge">En congé</option><option value="sorti">Sorti</option></NSelect></Field>
          </div>
          <Field label="Notes RH (confidentiel)" className="sm:col-span-2"><Textarea rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="emp-notes-input" /></Field>
        </div>
        <DialogFooter><Button className="bg-red-600 hover:bg-red-700" onClick={save} data-testid="emp-save-btn">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Employees() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const load = () => api.get("/employees").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const shown = items.filter((e) => `${e.first_name} ${e.last_name} ${e.position} ${e.department} ${e.matricule}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader kicker="Gestion du personnel" title="Employés">
        <Button className="bg-red-600 hover:bg-red-700" onClick={() => setOpen(true)} data-testid="new-employee-btn"><Plus className="mr-1 h-4 w-4" /> Nouvel employé</Button>
      </PageHeader>
      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input className="pl-9" placeholder="Rechercher un employé…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="employees-search-input" />
      </div>
      {shown.length ? (
        <TableWrap>
          <thead><tr><Th>Matricule</Th><Th>Nom</Th><Th>Poste</Th><Th>Département</Th><Th>Contrat</Th><Th>Embauche</Th><Th>Statut</Th></tr></thead>
          <tbody>
            {shown.map((e) => (
              <tr key={e.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/employes/${e.id}`)} data-testid={`employee-row-${e.matricule}`}>
                <Td className="font-mono text-xs">{e.matricule}</Td>
                <Td className="font-medium">{e.first_name} {e.last_name}</Td>
                <Td>{e.position}</Td>
                <Td className="text-slate-600">{e.department || "—"}</Td>
                <Td>{e.contract_type}</Td>
                <Td>{e.hire_date ? fdate(e.hire_date) : "—"}</Td>
                <Td><StatusBadge status={e.status} /></Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      ) : <Empty testId="employees-empty" text="Aucun employé trouvé." />}
      <EmployeeDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
